// src/oura/visibility/rivalFacts.ts
//
// What the Competitors tab can say about a rival beyond its three counts.
//
// "Named on 17, beats it on 8, both named on 9" says how big a rival is. It
// does not say how it wins, whether it is getting stronger, or which of the
// questions it takes are new. Every one of those is in data the scan already
// holds — the per-engine rivals on each row, the answer excerpts, and the
// previous scan — so this module counts them rather than asking for more.
//
// SAME RULES AS THE REST OF THE MODEL.
//   1. A fact the scan cannot support is `null`, never 0. "We could not read
//      the order" and "never named first" are opposite claims.
//   2. Counted from the same rows as the counts beside it, under the same
//      engine filter, so a fact can never disagree with the number it sits by.
//   3. A comparison with an earlier scan is made only when that scan asked the
//      same questions (the trend-break rule), and only across all engines:
//      one engine's figure from last month was never stored on its own.

import { ASSISTANTS, VIS_ENGINES, type EngineFilter, type VisEngine, type VisModel, type VisRival, type VisRow } from "./model";
import { VISIBILITY_CHECK_LABEL } from "../lib/visibilityChecks";

/** The previous comparable scan, reduced to what the tab compares against. */
export interface VisPriorScan {
    capturedAt: string;
    rivals: { name: string; on: number; winKeys: string[] }[];
    /**
     * Each question's verdict on that scan, by `row.key`, per engine. `null` is
     * an engine that scan did not measure. Absent on a prior scan that carries
     * no per-question detail (a fixture), which means no trend, not no change.
     */
    cells?: Record<string, Partial<Record<VisEngine, { named: boolean } | null>>>;
}

export interface RivalFacts {
    /** Of the questions it takes, how many named no other business at all. */
    sole: number | null;
    /** Of the questions it is named on, how many name it before anyone else. */
    first: number | null;
    /** Of the questions it is named on, how many both assistants named it on. */
    bothEngines: number | null;
    /** When only one assistant ever names it, which one. */
    soloEngine: VisEngine | null;
    /** Who the answers name beside it, this business first when it is there. */
    alongside: { name: string; count: number; you: boolean }[];
    /** Change in "Named on" since the previous comparable scan. */
    moved: { delta: number; isNew: boolean } | null;
    /** Row keys of the questions it takes now but did not take last time. */
    newWins: Set<string>;
}

const nameKey = (n: string) => n.trim().toLowerCase();
const sameName = (a: string, b: string) => nameKey(a) === nameKey(b);

/**
 * The engines a filter counts over: the ones THIS SCAN counted, or the one
 * engine chosen.
 *
 * The scan's own list, so "Beats it ... on questions where any engine names
 * them and not you" is true as written once BE-5 has recounted (#4025 FE-15),
 * and stays true of an older scan that was counted over the two assistants.
 */
function scope(model: VisModel, engine: EngineFilter): readonly VisEngine[] {
    return engine === "all" ? model.counted : [engine];
}

/** The rivals a row names under the filter: the assistants' merged list, or one engine's. */
function rivalsOnRow(row: VisRow, engine: EngineFilter): string[] {
    return engine === "all" ? row.rivals : row.cells[engine]?.rivals ?? [];
}

function namedOnRow(row: VisRow, engine: EngineFilter): boolean {
    return engine === "all" ? row.named : !!row.cells[engine]?.named;
}

/**
 * Whether `rival` is the first business an answer names, read from the stored
 * excerpt. Conservative on purpose: when this business was named but its name
 * cannot be found in the text, the order is unknown and the row does not
 * count. Better to under-claim a rival's lead than to invent one.
 */
function namedFirstIn(text: string, rival: string, others: string[], business: string | null): boolean {
    const hay = text.toLowerCase();
    const at = (n: string) => hay.indexOf(nameKey(n));
    const mine = at(rival);
    if (mine < 0) return false;
    if (business !== null) {
        const b = at(business);
        if (b < 0 || b < mine) return false;
    }
    return others.every((o) => sameName(o, rival) || at(o) < 0 || at(o) > mine);
}

/** The questions a rival is named on, under the filter. */
function rowsOn(model: VisModel, rival: VisRival, engine: EngineFilter): VisRow[] {
    return model.rows.filter((row) => rivalsOnRow(row, engine).some((n) => sameName(n, rival.name)));
}

export function rivalFacts(
    model: VisModel,
    rival: VisRival,
    engine: EngineFilter,
    businessName: string,
    prior: VisPriorScan | null,
): RivalFacts {
    const on = rowsOn(model, rival, engine);
    const winKeys = new Set(rival.wins.map((r) => r.key));

    // Sole: a question it takes where the answers named nobody else.
    const sole = rival.wins.length === 0
        ? null
        : rival.wins.filter((row) => rivalsOnRow(row, engine).every((n) => sameName(n, rival.name))).length;

    // First: needs an answer to read. No excerpt anywhere in scope, no claim.
    const engines = scope(model, engine);
    const readable = on.some((row) => engines.some((e) => row.excerpts[e]?.text));
    const first = !readable
        ? null
        : on.filter((row) =>
            engines.some((e) => {
                const text = row.excerpts[e]?.text;
                const cell = row.cells[e];
                if (!text || !cell || !cell.rivals.some((n) => sameName(n, rival.name))) return false;
                return namedFirstIn(text, rival.name, cell.rivals, cell.named ? businessName : null);
            }),
        ).length;

    // Both engines: only meaningful when both assistants answered per
    // question. Deliberately still the two ASSISTANTS and not the scan's full
    // list -- "named by both" is a claim about a pair, and widening it to
    // three would quietly change what the sentence means.
    const bothMeasured = engine === "all" && ASSISTANTS.every((e) => model.engines[e].perQuestion);
    const bothEngines = !bothMeasured
        ? null
        : on.filter((row) => ASSISTANTS.every((e) => row.cells[e]?.rivals.some((n) => sameName(n, rival.name)))).length;
    const namingEngines = ASSISTANTS.filter((e) => on.some((row) => row.cells[e]?.rivals.some((n) => sameName(n, rival.name))));
    const soloEngine = bothMeasured && namingEngines.length === 1 ? namingEngines[0] : null;

    // Alongside: this business on the questions both were named on, then the
    // other names those same answers carried.
    const counts = new Map<string, { name: string; count: number }>();
    on.forEach((row) =>
        rivalsOnRow(row, engine).forEach((n) => {
            if (sameName(n, rival.name)) return;
            const k = nameKey(n);
            const hit = counts.get(k) ?? { name: n, count: 0 };
            hit.count += 1;
            counts.set(k, hit);
        }),
    );
    const others = Array.from(counts.values()).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    const youCount = on.filter((row) => namedOnRow(row, engine)).length;
    const alongside = [
        ...(youCount > 0 ? [{ name: businessName, count: youCount, you: true }] : []),
        ...others.slice(0, 3).map((o) => ({ ...o, you: false })),
    ];

    // Movement and new wins: all engines only, and only against a scan that
    // asked the same questions (the caller passes null otherwise).
    const past = engine === "all" && prior ? prior.rivals.find((r) => sameName(r.name, rival.name)) ?? null : null;
    const moved = engine === "all" && prior
        ? past
            ? { delta: rival.on - past.on, isNew: false }
            : { delta: rival.on, isNew: true }
        : null;
    const newWins = new Set<string>();
    if (engine === "all" && prior) {
        const before = new Set(past?.winKeys ?? []);
        winKeys.forEach((k) => {
            if (!before.has(k)) newWins.add(k);
        });
    }

    return { sole, first, bothEngines, soloEngine, alongside, moved, newWins };
}

/**
 * The one fact a rival's row in the table carries: the strongest thing known
 * about how it wins. Never a second count of something the columns show.
 *
 * A rival only ever named beside the business is never "first" in a way that
 * costs anything, so its line says what "first" means there: named above the
 * business in an answer that names both. "Named first" alone, under a heading
 * that says "never instead", read as a threat to the owners who tested it.
 */
export function rowFact(f: RivalFacts, rival: VisRival, who: "it" | "you", engineLabel: (e: VisEngine) => string): string | null {
    if (rival.takes > 0) {
        if (f.sole && f.sole > 0) return `Only name in ${pct(f.sole, rival.takes)}`;
        if (f.first && f.first > 0) return `Named first in ${pct(f.first, rival.on)}`;
    } else if (f.first && f.first > 0) {
        return `Above ${who} in ${pct(f.first, rival.on)}`;
    }
    if (f.soloEngine) return `${engineLabel(f.soloEngine)} only`;
    return null;
}

/**
 * A share as a whole percentage. YW, 30 Sep 2026: "7 of 8" and "14 of 17" do
 * not compare across rivals of different sizes, a share does. The count stays
 * one hover away (`of()`), so the percentage is never a number nobody can check.
 */
export function pct(n: number, d: number): string {
    return d > 0 ? `${Math.round((n / d) * 100)}%` : "–";
}

export const of = (n: number, d: number, what: string) => `${n} of ${d} ${what}`;

/* ------------------------------------------------------------------------- *
 * Why they win: a short read behind the numbers
 * ------------------------------------------------------------------------- */

export interface WhyStat {
    value: string;
    label: string;
    /** The count behind the percentage, for the tooltip and screen readers. */
    detail: string;
}

export interface WhyRead {
    /** One or two plain sentences, built only from the signals below. */
    summary: string;
    stats: WhyStat[];
    /** How the answers themselves describe the rival, quoted, when they do. */
    described: string | null;
}

const domainOf = (url: string) => {
    try {
        return new URL(url).hostname.replace(/^www\./, "").toLowerCase();
    } catch {
        return url.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0].toLowerCase();
    }
};

/**
 * The sentence an answer spends on the rival: the text right after its name,
 * to the end of that sentence ("Baba's Kitchen. Known for family and group
 * dining, with set menus for 10 or more."). Quoted, never paraphrased, so it
 * cannot say more than the answer did.
 *
 * Only a sentence about THIS rival, and only a whole one (#4234). In a table or
 * a list the text after a name is the next cell or the next row, which is
 * another business, so a name followed straight away by `|` or a line break
 * has no sentence, and the search never reads past the end of its own line or
 * cell. A stretch that ends with the answer itself and no full stop was cut
 * off by the backend's length limit, and is dropped rather than quoted mid-word.
 */
export function describedIn(text: string, name: string): string | null {
    const plain = text.replace(/\*\*/g, "");
    const trimmedName = name.trim();
    if (!trimmedName) return null;

    // The first mention that is the rival's own name and has something after it
    // on its line or cell: not the tail of a longer word ("Ali" inside "Akarali"),
    // and not one that is straight away followed by the next cell or row. An
    // unusable first mention does not hide a later, usable one.
    //
    // Searched in the ORIGINAL text with a case-insensitive pattern, never in a
    // lower-cased copy: lower-casing can change a string's length ("İzmir"), and
    // offsets from the copy would then land past a `|` in the original and quote
    // the next cell (#4234).
    const escapedName = trimmedName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const mentionPattern = new RegExp(escapedName, "giu");
    const isWordCharacter = (value: string | undefined) => value !== undefined && /[\p{L}\p{N}]/u.test(value);
    let at = -1;
    let after = "";
    let match: RegExpExecArray | null;
    while ((match = mentionPattern.exec(plain)) !== null) {
        const candidate = match.index;
        const matchedName = match[0];
        const matchedCharacters = Array.from(matchedName);
        const before = Array.from(plain.slice(0, candidate)).pop();
        const candidateAfter = plain.slice(candidate + matchedName.length);
        const following = Array.from(candidateAfter)[0];
        const startsInsideWord = isWordCharacter(matchedCharacters[0]) && isWordCharacter(before);
        const endsInsideWord = isWordCharacter(matchedCharacters[matchedCharacters.length - 1]) && isWordCharacter(following);
        if (!startsInsideWord && !endsInsideWord && !/^[ \t]*[|\n\r]/.test(candidateAfter)) {
            at = candidate;
            after = candidateAfter;
            break;
        }
    }
    if (at < 0) return null;
    const stop = after.search(/[|\n\r]/);
    const ownLine = stop < 0 ? after : after.slice(0, stop);
    const rest = ownLine.replace(/^[\s.,:;\-–—)]+/, "");
    const end = rest.search(/[.!?](\s|$)/);
    // Out of text with no full stop, and no line or cell end to say it is whole: cut off.
    if (end < 0 && stop < 0) return null;
    const line = (end < 0 ? rest : rest.slice(0, end + 1)).trim();
    if (line.length < 12 || line.length > 170) return null;
    // A list marker or the next name means the answer said nothing about it.
    if (/^\d+\./.test(line)) return null;
    return line.charAt(0).toUpperCase() + line.slice(1);
}

/**
 * Why a rival wins, from what the scan measured about the questions it takes:
 * which kind they are, whether this business's own site has a page for them,
 * which sites the answers lean on when they name the rival, and how the
 * answers describe it. Nothing about the rival's own site: Oura does not crawl
 * rivals, so their labelling or readability is not a thing this can say.
 */
export function whyTheyWin(
    model: VisModel,
    rival: VisRival,
    facts: RivalFacts,
    engine: EngineFilter,
    staff: boolean,
    businessName: string,
): WhyRead {
    const threat = rival.takes > 0;
    const base = threat ? rival.wins : rowsOn(model, rival, engine);
    const you = staff ? businessName : "you";
    const your = staff ? `${businessName}'s` : "your";
    const stats: WhyStat[] = [];

    // 1. How they win.
    if (threat && facts.sole && facts.sole > 0) {
        stats.push({ value: pct(facts.sole, rival.takes), label: "only name given", detail: of(facts.sole, rival.takes, "questions they take") });
    }
    if (facts.first && facts.first > 0) {
        stats.push(threat
            ? { value: pct(facts.first, rival.on), label: "named first", detail: of(facts.first, rival.on, "questions they are named on") }
            : { value: pct(facts.first, rival.on), label: staff ? "named above it" : "named above you", detail: of(facts.first, rival.on, "questions they are named on") });
    }

    // 2. Which kind of question.
    const kinds = new Map<string, number>();
    base.forEach((r) => { if (r.kind) kinds.set(r.kind, (kinds.get(r.kind) ?? 0) + 1); });
    const top = Array.from(kinds.entries()).sort((a, b) => b[1] - a[1])[0] ?? null;
    const topLabel = top ? (VISIBILITY_CHECK_LABEL[top[0] as keyof typeof VISIBILITY_CHECK_LABEL] ?? top[0]).toLowerCase() : null;
    if (top && topLabel) {
        stats.push({ value: pct(top[1], base.length), label: `${topLabel} questions`, detail: of(top[1], base.length, threat ? "questions they take" : "questions they are named on") });
    }

    // 3. This business's own site on those questions (the Coverage Set).
    const sited = base.filter((r) => r.site !== null);
    const missing = sited.filter((r) => r.site === "missing").length;
    const thin = sited.filter((r) => r.site === "partial").length;
    if (threat && sited.length > 0 && missing > 0) {
        stats.push({ value: pct(missing, sited.length), label: staff ? "no page on the site" : "no page on your site", detail: of(missing, sited.length, "questions they take") });
    }

    // 4. Where the answers get them from: the cited pages on the answers that
    //    name them. The logged-out ChatGPT app shows no links, so this is the
    //    web-grounded answers only, and silent when they cite nothing.
    const own = rival.domain ? domainOf(rival.domain) : null;
    const cited = new Map<string, number>();
    let ownHits = 0;
    let citing = 0;
    rowsOn(model, rival, engine).forEach((row) => {
        const pages = scope(model, engine).flatMap((e) =>
            row.cells[e]?.rivals.some((n) => sameName(n, rival.name)) ? row.excerpts[e]?.citedPages ?? [] : [],
        );
        if (pages.length === 0) return;
        citing += 1;
        const domains = new Set(pages.map(domainOf));
        if (own && domains.has(own)) ownHits += 1;
        domains.forEach((d) => { if (d !== own) cited.set(d, (cited.get(d) ?? 0) + 1); });
    });
    const topSites = Array.from(cited.entries()).sort((a, b) => b[1] - a[1]).slice(0, 2).map(([d]) => d);

    // 5. How the answers describe them: the most common sentence.
    const lines = new Map<string, number>();
    rowsOn(model, rival, engine).forEach((row) =>
        scope(model, engine).forEach((e) => {
            const t = row.excerpts[e]?.text;
            const line = t ? describedIn(t, rival.name) : null;
            if (line) lines.set(line, (lines.get(line) ?? 0) + 1);
        }),
    );
    const described = Array.from(lines.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;

    // The summary: each clause only when its signal is there and clear.
    const parts: string[] = [];
    if (threat) {
        let first = top && top[1] / base.length >= 0.5 ? `They win mostly ${topLabel} questions` : `They win across ${kinds.size || "several"} kinds of question`;
        if (sited.length > 0) {
            if (missing / sited.length >= 0.5) first += `, and ${your} site has no page for most of them`;
            else if ((missing + thin) / sited.length >= 0.5) first += `, where ${your} pages are thin or missing`;
            else first += `, even though ${your} site answers most of them`;
        }
        parts.push(`${first}.`);
    } else {
        let first = `Always named alongside ${you}, never in ${staff ? "its" : "your"} place`;
        if (facts.first && rival.on > 0 && facts.first / rival.on > 0.5) first += `, though usually above ${you}`;
        parts.push(`${first}.`);
    }
    if (citing > 0) {
        if (own && ownHits / citing >= 0.5) parts.push("The AI quotes their own website.");
        // "Not their own site" only when they have one on record to compare with.
        else if (topSites.length > 0) parts.push(`The AI names them from ${topSites.join(" and ")}${own ? ", not their own site" : ""}.`);
    }
    if (threat && facts.sole && rival.takes > 0 && facts.sole / rival.takes >= 0.75) {
        parts.push("Most of the time they are the only name given.");
    }

    return { summary: parts.join(" "), stats: stats.slice(0, 4), described };
}

/** Reduce a full model to what the next scan compares against. */
export function priorScanFrom(model: VisModel): VisPriorScan {
    const cells: NonNullable<VisPriorScan["cells"]> = {};
    model.rows.forEach((row) => {
        const per: Partial<Record<VisEngine, { named: boolean } | null>> = {};
        VIS_ENGINES.forEach((e) => {
            const c = row.cells[e];
            // A Google AI page still being read is not a miss on that scan.
            per[e] = c && !(e === "ai_overview" && row.googlePending) ? { named: c.named } : null;
        });
        cells[row.key] = per;
    });
    return {
        capturedAt: model.capturedAt,
        rivals: model.rivals.map((r) => ({ name: r.name, on: r.on, winKeys: r.wins.map((w) => w.key) })),
        cells,
    };
}
